import React, { useCallback, useId, useMemo } from 'react';
import {
  TAGS_SPECIALTY,
  TAGS_KNOWLEDGE_CONTENT_TYPE,
  TAGS_TARGET_AUDIENCE,
  TAGS_LAB_CONTENT_TYPE,
  TAGS_CHECKLIST_CONTENT_TYPE,
} from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useSelector } from 'react-redux';
import { RenderSection } from '../../components';
import { Tooltip } from 'src/components/core';
import { Select } from 'src/components/design-system/forms/select.component';
import { sortByAlphabet } from 'shared/utils';

const TAG_SELECTOR_TYPE = {
  KNOWLEDGE: 'KNOWLEDGE',
  LAB: 'LAB',
  CHECKLIST: 'CHECKLIST',
};

const names = {
  specialty: 'specialty',
  subspecialty: 'subspecialty',
  targetAudience: 'targetAudience',
  contentType: 'contentType',
};

export function RenderTags({
  selectedValues,
  errors,
  onChange,
  resetField,
  lockedFields = [],
  lockedTooltip = null,
}) {
  const LANG_TAGS = useSelector(getLang('TAGS'));
  const LANG_TAGS_FORM = useSelector(getLang('TAGS_FORM'));
  const type = TAG_SELECTOR_TYPE.KNOWLEDGE;

  const specialtyLabelId = useId();
  const subspecialtyLabelId = useId();
  const contentTypeLabelId = useId();
  const targetAudienceLabelId = useId();

  const placeholders = {
    specialty: LANG_TAGS_FORM.SPECIALTY_PLACEHOLDER,
    subspecialty: LANG_TAGS_FORM.SUBSPECIALTY_PLACEHOLDER,
    targetAudience: LANG_TAGS_FORM.TARGET_AUDIENCE_PLACEHOLDER,
    contentType: LANG_TAGS_FORM.CONTENT_TYPE_PLACEHOLDER,
  };

  const specialtyOptions = useMemo(
    () =>
      Object.values(TAGS_SPECIALTY)
        .map(el => ({
          value: el,
          label: LANG_TAGS[el],
        }))
        .sort((a, b) => sortByAlphabet(a?.label, b?.label)),
    [LANG_TAGS],
  );

  const subspecialtyOptions = useMemo(
    () =>
      Object.values(TAGS_SPECIALTY)
        .filter(el => el !== selectedValues.specialty)
        .map(el => ({
          value: el,
          label: LANG_TAGS[el],
        }))
        .sort((a, b) => sortByAlphabet(a?.label, b?.label)),

    [LANG_TAGS, selectedValues.specialty],
  );

  const isLocked = field => lockedFields.includes(field);

  const contentTypeOptions = useMemo(() => {
    let contentTypes = {};
    if (type === TAG_SELECTOR_TYPE.KNOWLEDGE) {
      contentTypes = TAGS_KNOWLEDGE_CONTENT_TYPE;
    }
    if (type === TAG_SELECTOR_TYPE.LAB) {
      contentTypes = TAGS_LAB_CONTENT_TYPE;
    }

    if (type === TAG_SELECTOR_TYPE.CHECKLIST) {
      contentTypes = TAGS_CHECKLIST_CONTENT_TYPE;
    }

    return Object.values(contentTypes)
      .map(el => ({
        value: el,
        label: LANG_TAGS[el],
      }))
      .sort((a, b) => sortByAlphabet(a?.label, b?.label));
  }, [LANG_TAGS, type]);

  const targetAudienceOptions = useMemo(
    () =>
      Object.values(TAGS_TARGET_AUDIENCE)
        .map(el => ({
          value: el,
          label: LANG_TAGS[el],
        }))
        .sort((a, b) => sortByAlphabet(a?.label, b?.label)),
    [LANG_TAGS],
  );

  const handleOnChange = useCallback(
    (field, value) => {
      if (!value) {
        resetField('subspecialty');
      }
      onChange(field, value);
    },
    [onChange, resetField],
  );

  return (
    <>
      <RenderSection>
        <h6 id={specialtyLabelId} data-key="tags-specialty-label">
          {placeholders.specialty}
        </h6>
        <Select
          value={selectedValues?.specialty}
          options={specialtyOptions}
          onChange={handleOnChange}
          name={names.specialty}
          placeholder={placeholders.specialty}
          aria-labelledby={specialtyLabelId}
          error={errors?.specialty}
        />
        <h6 id={subspecialtyLabelId} data-key="tags-subspecialty-label">
          {placeholders.subspecialty}
        </h6>
        <Select
          value={selectedValues?.subspecialty}
          options={subspecialtyOptions}
          onChange={onChange}
          name={names.subspecialty}
          placeholder={placeholders.subspecialty}
          aria-labelledby={subspecialtyLabelId}
          error={errors?.subspecialty}
          disabled={!selectedValues.specialty}
        />
      </RenderSection>
      <RenderSection>
        <h6 id={contentTypeLabelId} data-key="tags-content-type-label">
          {placeholders.contentType}
        </h6>
        <Tooltip title={isLocked(names.contentType) ? lockedTooltip : null} placement="top">
          {/* A disabled select takes no focus of its own, so the wrapper becomes the tooltip's
              trigger — otherwise keyboard users never reach the read-only explanation. */}
          <div
            role={isLocked(names.contentType) ? 'button' : undefined}
            tabIndex={isLocked(names.contentType) ? 0 : undefined}>
            <Select
              value={selectedValues?.contentType}
              options={contentTypeOptions}
              onChange={onChange}
              name={names.contentType}
              placeholder={placeholders.contentType}
              aria-labelledby={contentTypeLabelId}
              error={errors?.contentType}
              disabled={isLocked(names.contentType)}
            />
          </div>
        </Tooltip>
        <h6 id={targetAudienceLabelId} data-key="tags-target-audience-label">
          {placeholders.targetAudience}
        </h6>
        <Select
          mode="multiple"
          value={selectedValues?.targetAudience ?? []}
          options={targetAudienceOptions}
          onChange={onChange}
          name={names.targetAudience}
          placeholder={placeholders.targetAudience}
          aria-labelledby={targetAudienceLabelId}
          error={errors?.targetAudience}
          optionsRender
        />
      </RenderSection>
    </>
  );
}
